import React, { useContext } from 'react';
import {
  Accordion, AccordionDetails, AccordionSummary, Box, Chip, Paper, Typography,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { barColor, formatScore, scoreColor } from '../types';
import { ProgressBar } from '../../ui/ProgressBar';
import { SectionAccordionContext } from './SectionAccordionContext';

interface Props {
  title: string;
  /** A grade: drawn as a score chip and a bar. null renders as N/A. */
  score?: number | null;
  /** A non-percentage headline, for measures that are not grades. */
  valueLabel?: string;
  /** Replaces the score / value chip, for a headline that is neither. */
  headline?: React.ReactNode;
  summary?: string;
  children?: React.ReactNode;
}

const CARD_SX = { border: 1, borderColor: 'divider', mb: 2 };

const HeadlineChip: React.FC<Pick<Props, 'score' | 'valueLabel'>> = ({ score, valueLabel }) => {
  if (score !== undefined) return <Chip label={formatScore(score)} color={scoreColor(score)} />;
  return valueLabel ? <Chip label={valueLabel} variant="outlined" /> : null;
};

/**
 * The one frame every training-insights section shares: title, headline,
 * summary, body. Inside a SectionAccordionContext (the Details tab) it is a
 * collapsible accordion whose header row carries the title and headline;
 * anywhere else it is a plain card.
 */
const SectionShell: React.FC<Props> = ({ title, score, valueLabel, headline, summary, children }) => {
  const accordion = useContext(SectionAccordionContext);
  const headlineNode = headline ?? <HeadlineChip score={score} valueLabel={valueLabel} />;
  const body = (
    <>
      {summary && (
        <Typography variant="body2" color="text.secondary" mb={2}>{summary}</Typography>
      )}
      {/* An N/A grade (null: nothing of this kind in the track) has no bar to draw. */}
      {score != null && <ProgressBar value={score} tone={barColor(score)} className="mb-4" />}
      {children}
    </>
  );

  if (!accordion) {
    return (
      <Paper elevation={0} sx={{ p: 3, ...CARD_SX }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5} gap={2}>
          <Typography variant="h6">{title}</Typography>
          {headlineNode}
        </Box>
        {body}
      </Paper>
    );
  }

  return (
    <Accordion
      disableGutters
      elevation={0}
      expanded={accordion.isExpanded}
      onChange={accordion.onToggle}
      slotProps={{ transition: { unmountOnExit: true, onEntered: accordion.onExpanded } }}
      sx={{ ...CARD_SX, '&::before': { display: 'none' } }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        id={`${accordion.idBase}-header`}
        aria-controls={`${accordion.idBase}-content`}
        sx={{ px: 3, py: 1 }}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between" gap={2} width="100%" mr={1}>
          {/* The accordion already wraps this row in a heading element. */}
          <Typography variant="h6" component="span">{title}</Typography>
          {headlineNode}
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 3, pt: 0, pb: 3 }}>{body}</AccordionDetails>
    </Accordion>
  );
};

export default SectionShell;

/** "1m 06s" from seconds; "45s" under a minute. */
export const formatSeconds = (seconds: number | null | undefined): string => {
  if (seconds == null || Number.isNaN(seconds)) return '0s';
  const whole = Math.round(seconds);
  const minutes = Math.floor(whole / 60);
  const rest = whole % 60;
  return minutes ? `${minutes}m ${String(rest).padStart(2, '0')}s` : `${rest}s`;
};

/** A labelled figure in a row of figures. */
export const Figure: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Box>
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{label}</Typography>
    <Typography variant="subtitle1">{value}</Typography>
  </Box>
);

export const CLAMP_THREE_LINES = {
  display: '-webkit-box',
  WebkitLineClamp: 3,
  WebkitBoxOrient: 'vertical' as const,
  overflow: 'hidden',
};
